import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, MagnifyingGlass, Eye, Pencil, Trash, X, Printer, Users, FileText, ShoppingCart, Calculator } from '@phosphor-icons/react';
import { getInvoices, getInvoice, createInvoice, updateInvoice, getItems, getCustomers } from '../../utils/api';
import { formatPrice, formatDate, getStatusColor } from '../../utils/helpers';
import { toast } from 'sonner';
import InvoicePrintView from '../../components/admin/InvoicePrintView';

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const printRef = useRef(null);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${selectedInvoice?.invoice_number}</title>
          <style>
            body { margin: 0; padding: 20px; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_city: '',
    customer_pincode: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    tax_rate: 0,
    discount_amount: 0,
    shipping_charge: 0,
    packing_charge: 0,
    notes: '',
    items: [{ item_id: '', item_name: '', description: '', quantity: 1, unit_price: 0, mrp: 0 }]
  });

  const fetchItems = async () => {
    try {
      const res = await getItems({ limit: 100 });
      setItems(res.data.data || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: 10,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      
      const res = await getInvoices(params);
      setInvoices(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        totalPages: res.data.pagination?.total_pages || 1,
        total: res.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [pagination.page, search, statusFilter]);

  const handleViewInvoice = async (invoiceId) => {
    setLoadingInvoice(true);
    setShowModal(true);
    try {
      const res = await getInvoice(invoiceId);
      setSelectedInvoice(res.data.data);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
      setShowModal(false);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_city: '',
      customer_pincode: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      tax_rate: 0,
      discount_amount: 0,
      shipping_charge: 0,
      packing_charge: 0,
      notes: '',
      items: [{ item_id: '', item_name: '', description: '', quantity: 1, unit_price: 0, mrp: 0 }]
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const addInvoiceItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: '', item_name: '', description: '', quantity: 1, unit_price: 0, mrp: 0 }]
    });
  };

  const removeInvoiceItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    // If selecting from inventory, auto-fill details
    if (field === 'item_id' && value) {
      const selectedItem = items.find(item => item.id === parseInt(value));
      if (selectedItem) {
        newItems[index].item_name = selectedItem.name;
        newItems[index].unit_price = selectedItem.selling_rate;
        newItems[index].mrp = selectedItem.mrp || 0;
        newItems[index].description = selectedItem.description || '';
      }
    }

    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = (subtotal * formData.tax_rate) / 100;
    return subtotal + tax - (formData.discount_amount || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.customer_phone && formData.customer_phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }
    setPhoneError('');
    setSubmitting(true);

    try {
      const invoiceData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone || null,
        customer_email: formData.customer_email || null,
        customer_city: formData.customer_city || null,
        customer_pincode: formData.customer_pincode || null,
        invoice_date: formData.invoice_date || null,
        due_date: formData.due_date || null,
        tax_rate: parseFloat(formData.tax_rate) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        shipping_charge: parseFloat(formData.shipping_charge) || 0,
        packing_charge: parseFloat(formData.packing_charge) || 0,
        notes: formData.notes || null,
        items: formData.items.map(item => ({
          item_id: item.item_id ? parseInt(item.item_id) : null,
          item_name: item.item_name,
          description: item.description || null,
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          mrp: item.mrp ? parseFloat(item.mrp) : null
        }))
      };

      await createInvoice(invoiceData);
      toast.success('Invoice created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await updateInvoice(invoiceId, { status: newStatus });
      toast.success('Invoice status updated');
      fetchInvoices();
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        handleViewInvoice(invoiceId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Invoices</h1>
          <p className="font-body text-sm text-text-secondary">{pagination.total} total invoices</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPagination(p => ({...p, page: 1})); }}
              data-testid="invoices-search"
              className="w-full pl-10 pr-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPagination(p => ({...p, page: 1})); }}
            data-testid="status-filter"
            className="px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm bg-white"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
          </select>

          <button
            onClick={openCreateModal}
            data-testid="create-invoice-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-background-subtle">
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Invoice</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Customer</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Date</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Amount</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Status</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-border-default">
                    <td colSpan={6} className="py-4 px-4">
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </td>
                  </tr>
                ))
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <p className="font-body text-text-secondary">No invoices found</p>
                    <button onClick={openCreateModal} className="btn-secondary mt-4">
                      Create First Invoice
                    </button>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <motion.tr
                    key={invoice.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-border-default hover:bg-background-subtle transition-colors"
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-secondary font-medium">
                        {invoice.invoice_number}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-body text-sm text-secondary">{invoice.customer_name}</p>
                        <p className="font-body text-xs text-text-muted">{invoice.customer_phone || invoice.customer_email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-text-secondary">
                        {formatDate(invoice.invoice_date)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div>
                        <p className="font-body text-sm text-secondary">{formatPrice(invoice.total_amount)}</p>
                        {invoice.balance_amount > 0 && (
                          <p className="font-body text-xs text-red-500">
                            Due: {formatPrice(invoice.balance_amount)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <select
                        value={invoice.status}
                        onChange={(e) => handleStatusChange(invoice.id, e.target.value)}
                        className={`font-body text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${getStatusColor(invoice.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="partial">Partial</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewInvoice(invoice.id)}
                          data-testid={`view-invoice-${invoice.id}`}
                          className="p-2 text-text-muted hover:text-primary transition-colors"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border-default">
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => (
              <button
                key={i}
                onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                className={`w-8 h-8 font-body text-sm transition-colors ${
                  pagination.page === i + 1 
                    ? 'bg-primary text-white' 
                    : 'bg-background-subtle text-secondary hover:bg-primary/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default sticky top-0 bg-white">
              <h3 className="font-display text-xl text-secondary">Invoice Details</h3>
              <button onClick={() => { setShowModal(false); setSelectedInvoice(null); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            {loadingInvoice ? (
              <div className="p-6 space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-8 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            ) : selectedInvoice && (
              <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-display text-2xl text-secondary">{selectedInvoice.invoice_number}</h4>
                    <p className="font-body text-sm text-text-muted">
                      Date: {formatDate(selectedInvoice.invoice_date)}
                    </p>
                  </div>
                  <span className={`font-body text-sm px-3 py-1 rounded-full capitalize ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>

                {/* Customer Info */}
                <div className="p-4 bg-background-subtle">
                  <h5 className="font-body text-xs uppercase text-text-muted mb-2">Customer</h5>
                  <p className="font-body text-sm text-secondary">{selectedInvoice.customer_name}</p>
                  {selectedInvoice.customer_phone && (
                    <p className="font-body text-sm text-text-secondary">{selectedInvoice.customer_phone}</p>
                  )}
                  {selectedInvoice.customer_email && (
                    <p className="font-body text-sm text-text-secondary">{selectedInvoice.customer_email}</p>
                  )}
                  {selectedInvoice.customer_city && (
                    <p className="font-body text-sm text-text-secondary">
                      {selectedInvoice.customer_city} {selectedInvoice.customer_pincode}
                    </p>
                  )}
                </div>

                {/* Items */}
                <div>
                  <h5 className="font-body text-xs uppercase text-text-muted mb-3">Items</h5>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-default">
                        <th className="text-left font-body text-xs text-text-secondary py-2">Item</th>
                        <th className="text-center font-body text-xs text-text-secondary py-2">Qty</th>
                        <th className="text-right font-body text-xs text-text-secondary py-2">Price</th>
                        {selectedInvoice.items?.some(item => item.mrp) && (
                          <th className="text-right font-body text-xs text-text-secondary py-2">MRP</th>
                        )}
                        <th className="text-right font-body text-xs text-text-secondary py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items?.map((item, index) => (
                        <tr key={index} className="border-b border-border-default">
                          <td className="py-2 font-body text-sm text-secondary">{item.item_name}</td>
                          <td className="py-2 text-center font-body text-sm text-text-secondary">{item.quantity}</td>
                          <td className="py-2 text-right font-body text-sm text-text-secondary">{formatPrice(item.unit_price)}</td>
                          {selectedInvoice.items?.some(item => item.mrp) && (
                            <td className="py-2 text-right font-body text-sm text-text-muted">
                              {item.mrp ? formatPrice(item.mrp) : '-'}
                            </td>
                          )}
                          <td className="py-2 text-right font-body text-sm text-secondary">{formatPrice(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t border-border-default pt-4 space-y-2">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-secondary">Subtotal</span>
                    <span className="text-secondary">{formatPrice(selectedInvoice.subtotal)}</span>
                  </div>
                  {selectedInvoice.tax_amount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-text-secondary">Tax ({selectedInvoice.tax_rate}%)</span>
                      <span className="text-secondary">{formatPrice(selectedInvoice.tax_amount)}</span>
                    </div>
                  )}
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-text-secondary">Discount</span>
                      <span className="text-green-600">-{formatPrice(selectedInvoice.discount_amount)}</span>
                    </div>
                  )}
                  {selectedInvoice.shipping_charge > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-text-secondary">Shipping</span>
                      <span className="text-secondary">{formatPrice(selectedInvoice.shipping_charge)}</span>
                    </div>
                  )}
                  {selectedInvoice.packing_charge > 0 && (
                    <div className="flex justify-between font-body text-sm">
                      <span className="text-text-secondary">Packing</span>
                      <span className="text-secondary">{formatPrice(selectedInvoice.packing_charge)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-display text-lg pt-2 border-t border-border-default">
                    <span className="text-secondary">Total</span>
                    <span className="text-primary">{formatPrice(selectedInvoice.total_amount)}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-secondary">Paid</span>
                    <span className="text-green-600">{formatPrice(selectedInvoice.paid_amount)}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-text-secondary">Balance</span>
                    <span className={selectedInvoice.balance_amount > 0 ? 'text-red-500' : 'text-green-600'}>
                      {formatPrice(selectedInvoice.balance_amount)}
                    </span>
                  </div>
                </div>

                {/* Print Button */}
                <div className="pt-4 border-t border-border-default">
                  <button
                    onClick={handlePrint}
                    data-testid="print-invoice-btn"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <Printer size={18} />
                    Print / Export PDF
                  </button>
                </div>

                {/* Hidden Print View */}
                <div className="hidden">
                  <InvoicePrintView ref={printRef} invoice={selectedInvoice} />
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default sticky top-0 bg-white z-10">
              <h3 className="font-display text-xl text-secondary">Create Invoice</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-8">
              {/* Customer Details */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users size={16} className="text-primary" />
                  </div>
                  <h4 className="font-display text-lg text-secondary">Customer Details</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-background-subtle p-4 rounded-lg">
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Customer Name *</label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      data-testid="invoice-customer-name"
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="Customer name"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Phone *</label>
                    <input
                      type="text"
                      value={formData.customer_phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, customer_phone: value });
                        setPhoneError('');
                      }}
                      data-testid="invoice-customer-phone"
                      className={`w-full px-4 py-2.5 border rounded focus:outline-none font-body text-sm bg-white ${phoneError ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                      placeholder="10-digit phone number"
                      maxLength={10}
                      required
                    />
                    {phoneError && <p className="text-red-500 text-xs mt-1 ml-1">{phoneError}</p>}
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Email</label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      data-testid="invoice-customer-email"
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="Email address"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">City</label>
                    <input
                      type="text"
                      value={formData.customer_city}
                      onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="City"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <FileText size={16} className="text-primary" />
                  </div>
                  <h4 className="font-display text-lg text-secondary">Invoice Details</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-background-subtle p-4 rounded-lg">
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Invoice Date</label>
                    <input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Due Date</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Tax Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <ShoppingCart size={16} className="text-primary" />
                    </div>
                    <h4 className="font-display text-lg text-secondary">Items</h4>
                  </div>
                  <button
                    type="button"
                    onClick={addInvoiceItem}
                    className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-body flex items-center gap-1 hover:bg-primary/90 transition-colors"
                  >
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="bg-background-subtle rounded-lg p-4">
                      <div className="grid grid-cols-12 gap-3 items-end">
                        <div className="col-span-12 md:col-span-4">
                          <label className="block font-body text-xs text-text-muted mb-1 ml-1">Select Item</label>
                          <select
                            value={item.item_id}
                            onChange={(e) => updateInvoiceItem(index, 'item_id', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                          >
                            <option value="">-- Custom Item --</option>
                            {items.map((invItem) => (
                              <option key={invItem.id} value={invItem.id}>
                                {invItem.name} - {formatPrice(invItem.selling_rate)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12 md:col-span-4">
                          <label className="block font-body text-xs text-text-muted mb-1 ml-1">Item Name *</label>
                          <input
                            type="text"
                            value={item.item_name}
                            onChange={(e) => updateInvoiceItem(index, 'item_name', e.target.value)}
                            required
                            className="w-full px-3 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm"
                            placeholder="Item name"
                          />
                        </div>
                        <div className="col-span-3 md:col-span-2">
                          <label className="block font-body text-xs text-text-muted mb-1 ml-1">Qty *</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)}
                            required
                            className="w-full px-3 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm text-center"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-2">
                          <label className="block font-body text-xs text-text-muted mb-1 ml-1">Price *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateInvoiceItem(index, 'unit_price', e.target.value)}
                            required
                            className="w-full px-3 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-1">
                          <label className="block font-body text-xs text-text-muted mb-1 ml-1">MRP</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.mrp || ''}
                            onChange={(e) => updateInvoiceItem(index, 'mrp', e.target.value)}
                            className="w-full px-3 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-1 flex items-center justify-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeInvoiceItem(index)}
                              className="p-2.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Remove item"
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-between items-center pt-2 border-t border-border-default/50">
                        <span className="text-xs text-text-muted">Line Total</span>
                        <span className="font-display text-base text-primary font-medium">
                          {formatPrice(item.quantity * item.unit_price)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary & Charges */}
              <div className="bg-background-subtle rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <Calculator size={16} className="text-primary" />
                  </div>
                  <h4 className="font-display text-lg text-secondary">Summary & Charges</h4>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Discount Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discount_amount}
                      onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Shipping (Internal)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.shipping_charge}
                      onChange={(e) => setFormData({ ...formData, shipping_charge: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Packing (Internal)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.packing_charge}
                      onChange={(e) => setFormData({ ...formData, packing_charge: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs text-text-muted mb-1 ml-1">Notes</label>
                    <input
                      type="text"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-4 py-2.5 border border-border-default rounded focus:border-primary focus:outline-none font-body text-sm bg-white"
                      placeholder="Invoice notes..."
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-white rounded-lg p-4 border border-border-default">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="font-body text-sm text-text-secondary">Subtotal</span>
                      <span className="font-body text-sm text-secondary font-medium">{formatPrice(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="font-body text-sm text-text-secondary">Tax ({formData.tax_rate}%)</span>
                      <span className="font-body text-sm text-secondary font-medium">{formatPrice((calculateSubtotal() * formData.tax_rate) / 100)}</span>
                    </div>
                    {formData.discount_amount > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="font-body text-sm text-text-secondary">Discount</span>
                        <span className="font-body text-sm text-green-600 font-medium">-{formatPrice(formData.discount_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-3 border-t-2 border-border-default mt-2">
                      <span className="font-display text-lg text-secondary">Total Amount</span>
                      <span className="font-display text-xl text-primary font-bold">{formatPrice(calculateTotal())}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 pt-2">
                <button 
                  type="button" 
                  onClick={() => { setShowCreateModal(false); resetForm(); }} 
                  className="btn-secondary flex-1 py-3"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-invoice-btn" 
                  className="btn-primary flex-1 py-3 disabled:opacity-50 text-base"
                >
                  {submitting ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
